/** Spec §3.1. Public contract for the identity layer. */
export interface Addresses {
  cere: string;
  solana: string;
  evm: string;
}

export type Chain = 'cere' | 'solana' | 'evm';

/**
 * The S0 account this session belongs to, as the server reports it on every
 * session response (register-finish, login-finish, session refresh).
 *
 * An account is the durable identity; a credential (passkey) is one way into
 * it. `email`/`displayName` are nullable because accounts created before S0 have
 * neither — the migration does not invent them, and the Settings nudge asks for
 * an email without ever gating on one.
 */
export interface AccountInfo {
  accountId: string;
  email: string | null;
  displayName: string | null;
}

/*
 * S1 PR2 deleted `IdentitySession`, `SessionHandoff` and
 * `supportsSessionHandoff`.
 *
 * `IdentitySession` was the wire shape of a session moving between two
 * wallet-origin surfaces: raw `edSeed` and `secpKey` bytes, plus the bearer
 * token. `SessionHandoff` was the pair of methods that produced and consumed
 * it. Together they were the ONLY API by which key material could leave an
 * `Identity` in usable form — `exportKeystore` hands out ciphertext the caller
 * must supply a passphrase to open, which is a different thing.
 *
 * Removing them is what makes "keys never cross a surface boundary" structural
 * instead of a convention: the registering popup no longer exports anything,
 * because the frame re-derives its own key from PRF. `no-key-export.test.ts`
 * is the standing guard against the seam being reintroduced.
 */

/**
 * Bearer-only session resume capability (Task 2/3). Kept OFF the core
 * `Identity` interface deliberately: it is an
 * optional, refresh-flow-only concern, and forcing every `Identity`
 * implementation (including thin test doubles used across the SPA's test
 * suite) to provide it would widen the interface for no benefit to callers
 * that never boot the embed route. Concrete `IdentityImpl` implements it;
 * SPA wiring feature-detects via `supportsSessionResume()`.
 */
export interface SessionResume {
  /**
   * Adopt a session from `apiClient.session.refresh()` WITHOUT running a
   * passkey ceremony and WITHOUT deriving signing keys — the "bearer-active"
   * state. See `IdentityImpl.resumeSession` for the full contract.
   */
  resumeSession(session: {
    token: string;
    addresses: Addresses;
    credentialId: string;
    /**
     * The account fields off the same `SessionResponse` the token came from, so
     * a bearer-resumed tab knows its account without a ceremony. Optional so a
     * caller that spreads a whole `SessionResponse` gets them for free while an
     * older, narrower call site still compiles; when `accountId` is absent the
     * previously-known account is left alone rather than blanked.
     */
    accountId?: string;
    email?: string | null;
    displayName?: string | null;
  }): void;
  /**
   * `true` once real signing keys exist (register/login/adoptSession); `false`
   * for a bearer-active session with a token but no keys.
   */
  hasKeys(): boolean;
}

/**
 * Narrow an `Identity` to one that supports bearer-only session resume.
 * `IdentityImpl` returns true; thin test doubles return false and are simply
 * skipped by boot-time resume wiring (e.g. the embed route falls back to
 * "stay logged out" rather than crashing on a missing method).
 */
export function supportsSessionResume(identity: Identity): identity is Identity & SessionResume {
  return (
    typeof (identity as Partial<SessionResume>).resumeSession === 'function' &&
    typeof (identity as Partial<SessionResume>).hasKeys === 'function'
  );
}

/**
 * SPA-injected keystore encoder. Lives OUTSIDE the published identity package
 * so the heavy keystore libraries (`@polkadot/keyring`, `ethers`) stay in the
 * SPA bundle only. Given raw key bytes it returns a password-encrypted keystore
 * JSON string. Called synchronously inside `IdentityImpl`'s `sessionKeys` seam
 * (see `WalletExport`); it MUST be pure and retain nothing.
 *
 * - `chain: 'cere'` → keyBytes is the 32-byte ed25519 seed → Polkadot ed25519
 *   keystore JSON (SS58 prefix 54).
 * - `chain: 'evm'`  → keyBytes is the 32-byte secp256k1 key → Ethereum V3 keystore JSON.
 */
export type KeystoreEncryptor = (
  chain: 'cere' | 'evm',
  keyBytes: Uint8Array,
  passphrase: string,
  address: string,
) => Promise<string>;

/**
 * Wallet-export capability. Kept OFF the core `Identity` interface for the same
 * reason as `SessionResume`: it is an optional,
 * standalone-SPA-only concern. Concrete `IdentityImpl` implements it; UI wiring
 * feature-detects via `supportsWalletExport()`.
 *
 * SECURITY: `exportKeystore` returns only ciphertext — raw seeds never leave the
 * vault seam. `revealSolanaSecret` is the sole raw-key path (Phantom cannot
 * import an encrypted file); it is deliberately a separate, explicitly-named
 * method so the raw path cannot be taken by accident.
 */
export interface WalletExport {
  /** Password-encrypted keystore JSON string for the given chain. */
  exportKeystore(chain: 'cere' | 'evm', passphrase: string): Promise<string>;
  /** base58-encoded 64-byte Solana secret key (raw — Phantom import). */
  revealSolanaSecret(): Promise<string>;
}

/**
 * Narrow an `Identity` to one that supports wallet export. `IdentityImpl`
 * returns true; thin test doubles return false and hide the export UI.
 */
export function supportsWalletExport(identity: Identity): identity is Identity & WalletExport {
  return (
    typeof (identity as Partial<WalletExport>).exportKeystore === 'function' &&
    typeof (identity as Partial<WalletExport>).revealSolanaSecret === 'function'
  );
}

export interface Identity {
  readonly isAuthenticated: boolean;
  readonly addresses: Addresses | null;
  readonly credentialId: string | null;
  /**
   * The account behind the current session, or `null` when signed out. Set by
   * register / login / `resumeSession` from the server's session response — a
   * pure read, never a ceremony trigger.
   *
   * Optional on the interface (not on `IdentityImpl`, which always defines it)
   * so the SPA's thin `Identity` test doubles are not all forced to carry an
   * account they do not model.
   */
  readonly account?: AccountInfo | null;

  /**
   * Replace the account fields for the current session (see
   * `IdentityImpl.setAccount`). The only caller is a successful
   * `PATCH /v1/accounts/me`, which returns the whole updated account and is
   * the one way the account changes WITHOUT a session response to carry it.
   *
   * Optional for the same reason `account` is: the SPA's thin `Identity` test
   * doubles do not model an account, and a caller must therefore treat this
   * as a best-effort refresh (`identity.setAccount?.(...)`), never as the
   * thing that makes the patch durable — the server already did that.
   */
  setAccount?(account: AccountInfo): void;

  /**
   * Create a NEW account and enrol this device's passkey as its ORIGIN
   * credential — the one whose PRF output IS the account root (described
   * under "`is_origin`" in `ARCHITECTURE.md`, which lives in the wallet
   * repo, not in this package). Registration writes no root
   * envelope: adding a further device is recover-then-enrol, a later
   * sub-project, not a second `register()`.
   *
   * `emailToken` comes from `accounts.emailVerify` — S0 proves the mailbox
   * BEFORE the ceremony, so there is no half-registered account to clean up
   * and a conflicting address is disclosed only to someone who controls it.
   * It is REQUIRED: there is no registration path that does not begin with a
   * verified mailbox. `IdentityImpl` still rejects an empty one by name
   * (`email-unverified`) as defence in depth for JS callers the type cannot
   * reach.
   *
   * `displayName` is the account's display name, and becomes the WebAuthn
   * `user.displayName` hint.
   *
   * `email` is the address the caller just proved control of, and becomes the
   * WebAuthn `user.name` hint — the line an OS passkey manager shows as the
   * account. It is optional only because JS callers exist that cannot supply
   * it; omitting it leaves the entry reading `scp-wallet`, which identifies
   * nothing (CEF-AI/wallet#72). Pass the address that `emailToken` attests,
   * never a separately-typed one.
   *
   * Neither field is transmitted: the WebAuthn user entity is consumed by the
   * authenticator and never appears in `clientDataJSON` or the attestation, so
   * `email` here creates no second, unverified copy server-side. It is
   * persisted in the resident credential and so may sync to the platform's
   * passkey store (iCloud Keychain and equivalents) — which is where a passkey
   * manager has to read it from to show it at all.
   */
  register(opts: { emailToken: string; displayName?: string; email?: string; label?: string }): Promise<void>;
  /**
   * `opts.credentialId`, when supplied, pins the WebAuthn `allowCredentials`
   * to that credential (no discoverable-credential picker) instead of the
   * vault-snapshot credential this identity would otherwise fall back to.
   * Used by the step-up ceremony (Task 5 fix), where the vault is closed —
   * so there is no vault-snapshot credential to pin to — but the caller
   * (the SDK) already knows which credential the active session belongs to
   * and must not let the ceremony silently authenticate a different one.
   * Omitted for a normal login()/step-up-of-a-fresh-session, unchanged from
   * prior behavior.
   */
  login(opts?: { credentialId?: string }): Promise<void>;
  logout(): void;

  /** Returns a valid JWT; runs the passkey ceremony if needed. */
  getJwt(): Promise<string>;

  /**
   * Release any browser-owned handles this identity holds (cross-tab channel,
   * timers, broadcast listeners). After `dispose()` the identity is unusable.
   *
   * Idempotent: callable multiple times safely.
   */
  dispose(): void;
}

/**
 * Signers/* receive this function via a constructor-time closure handoff
 * from a concrete Identity instance. It is exported as a type-only alias so
 * dependents can annotate the closures they receive, but it is intentionally
 * absent from the `Identity` interface — there is no public `sign()` method.
 */
export type InternalSign = (chain: Chain, payload: Uint8Array) => Promise<Uint8Array>;
